import type { Customer } from "@pilatestime/shared-types";
import { NotificationType } from "@pilatestime/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Copy,
	FileText,
	Gift,
	HelpCircle,
	Mail,
	Plus,
	Search,
	Trash2,
	UserPlus,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationCustomersPaginatedQueryOptions } from "@/api/customers";
import {
	fetchNotificationConfigQueryOptions,
	fetchNotificationExclusionsQueryOptions,
	useAddNotificationExclusion,
	useRemoveNotificationExclusion,
	useUpdateNotificationConfig,
} from "@/api/notifications";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { ToggleSwitch } from "@/components/ui/toggle-switch";
import { useApiParameters } from "@/hooks/useApiParameters";

// Available template variables for birthday notifications
const TEMPLATE_VARIABLES = [
	{ variable: "{{customerName}}", description: "Customer's full name" },
	{ variable: "{{customerFirstName}}", description: "Customer's first name" },
	{ variable: "{{customerLastName}}", description: "Customer's last name" },
	{ variable: "{{organizationName}}", description: "Organization name" },
] as const;

// Predefined email templates for birthday notifications
const EMAIL_TEMPLATES = {
	en_US: [
		{
			subject: "🎂 Happy Birthday from {{organizationName}}!",
			body: `Dear {{customerFirstName}},

Wishing you a wonderful birthday filled with joy and happiness!

On this special day, the entire team at {{organizationName}} sends you our warmest wishes.

Have a fantastic celebration!

Best regards,
{{organizationName}}`,
		},
		{
			subject: "Happy Birthday, {{customerFirstName}}! 🎉",
			body: `Hello {{customerName}},

It's your special day and we couldn't let it pass without sending you our best wishes!

May this new year of life bring you health, happiness, and many achievements.

Warm regards,
The {{organizationName}} Team`,
		},
		{
			subject: "Birthday Wishes from {{organizationName}} 🎈",
			body: `Hi {{customerFirstName}},

Happy Birthday! We hope your day is as amazing as you are.

Thank you for being part of our community. Enjoy your celebration!

With love,
{{organizationName}}`,
		},
	],
	pt_BR: [
		{
			subject: "🎂 Feliz Aniversário da {{organizationName}}!",
			body: `Querido(a) {{customerFirstName}},

Desejamos a você um aniversário maravilhoso repleto de alegria e felicidade!

Neste dia especial, toda a equipe da {{organizationName}} envia os mais calorosos votos.

Tenha uma comemoração fantástica!

Com carinho,
{{organizationName}}`,
		},
		{
			subject: "Feliz Aniversário, {{customerFirstName}}! 🎉",
			body: `Olá {{customerName}},

É o seu dia especial e não poderíamos deixar passar sem enviar nossos melhores votos!

Que este novo ano de vida lhe traga saúde, felicidade e muitas conquistas.

Abraços,
Equipe {{organizationName}}`,
		},
		{
			subject: "Votos de Aniversário da {{organizationName}} 🎈",
			body: `Oi {{customerFirstName}},

Feliz Aniversário! Esperamos que seu dia seja tão incrível quanto você.

Obrigado por fazer parte da nossa comunidade. Aproveite sua comemoração!

Com carinho,
{{organizationName}}`,
		},
	],
} as const;

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/events/birthday-reminder/",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token };
	},
	component: BirthdayReminderConfigPage,
});

function BirthdayReminderConfigPage() {
	const { t, i18n } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	// Fetch notification config
	const { data: config } = useSuspenseQuery(
		fetchNotificationConfigQueryOptions(
			userId,
			token,
			orgId,
			NotificationType.BIRTHDAY_REMINDER,
		),
	);

	// Fetch exclusions (first page)
	const { data: exclusionsData } = useSuspenseQuery(
		fetchNotificationExclusionsQueryOptions(
			userId,
			token,
			orgId,
			NotificationType.BIRTHDAY_REMINDER,
			1,
			10,
		),
	);

	// Local state for form - Master switch
	const [enabled, setEnabled] = useState(config.enabled);

	// Email channel state
	const [emailEnabled, setEmailEnabled] = useState(config.emailEnabled);
	const [messageSubject, setMessageSubject] = useState(
		config.messageSubject ?? "",
	);
	const [messageBody, setMessageBody] = useState(config.messageBody ?? "");

	// SMS channel state (UI hidden — values preserved from existing config)
	const [smsEnabled] = useState(config.smsEnabled);
	const [smsBody] = useState(config.smsBody ?? "");

	// WhatsApp channel state (UI hidden — values preserved from existing config)
	const [whatsappEnabled] = useState(config.whatsappEnabled);
	const [whatsappBody] = useState(config.whatsappBody ?? "");

	// Add customer modal state
	const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
	const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
		null,
	);
	const [exclusionReason, setExclusionReason] = useState("");

	// Template cycling state
	const [templateIndex, setTemplateIndex] = useState(0);

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Reset modal state when closed
	useEffect(() => {
		if (!isAddCustomerOpen) {
			setSearchTerm("");
			setDebouncedSearchTerm("");
			setSelectedCustomer(null);
			setExclusionReason("");
		}
	}, [isAddCustomerOpen]);

	// Fetch customers for search
	const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
		...fetchOrganizationCustomersPaginatedQueryOptions(
			userId,
			token,
			orgId,
			{ page: 1, limit: 15 },
			debouncedSearchTerm || undefined,
		),
		enabled: isAddCustomerOpen && debouncedSearchTerm.length >= 2,
	});

	const customers = customersData?.data || [];

	// Get currently excluded customer IDs
	const excludedCustomerIds = new Set(
		exclusionsData.data.map((e) => e.customerId),
	);

	// Mutations
	const updateConfigMutation = useUpdateNotificationConfig();
	const removeExclusionMutation = useRemoveNotificationExclusion();
	const addExclusionMutation = useAddNotificationExclusion();

	const handleSave = () => {
		updateConfigMutation.mutate({
			token,
			organizationId: orgId,
			notificationType: NotificationType.BIRTHDAY_REMINDER,
			data: {
				enabled,
				// Email channel
				emailEnabled,
				messageSubject: messageSubject || null,
				messageBody: messageBody || null,
				// SMS channel
				smsEnabled,
				smsBody: smsBody || null,
				// WhatsApp channel
				whatsappEnabled,
				whatsappBody: whatsappBody || null,
			},
		});
	};

	const handleRemoveExclusion = (customerId: string) => {
		removeExclusionMutation.mutate({
			token,
			organizationId: orgId,
			notificationType: NotificationType.BIRTHDAY_REMINDER,
			customerId,
		});
	};

	const handleAddExclusion = () => {
		if (!selectedCustomer) return;

		addExclusionMutation.mutate(
			{
				token,
				organizationId: orgId,
				notificationType: NotificationType.BIRTHDAY_REMINDER,
				data: {
					customerId: selectedCustomer.id,
					reason: exclusionReason || undefined,
				},
			},
			{
				onSuccess: () => {
					setIsAddCustomerOpen(false);
				},
			},
		);
	};

	const hasChanges =
		enabled !== config.enabled ||
		// Email channel
		emailEnabled !== config.emailEnabled ||
		messageSubject !== (config.messageSubject ?? "") ||
		messageBody !== (config.messageBody ?? "") ||
		// SMS channel
		smsEnabled !== config.smsEnabled ||
		smsBody !== (config.smsBody ?? "") ||
		// WhatsApp channel
		whatsappEnabled !== config.whatsappEnabled ||
		whatsappBody !== (config.whatsappBody ?? "");

	const handlePopulateFromTemplate = () => {
		const lang = i18n.language === "pt_BR" ? "pt_BR" : "en_US";
		const templates = EMAIL_TEMPLATES[lang];
		const template = templates[templateIndex % templates.length];
		setMessageSubject(template.subject);
		setMessageBody(template.body);
		setTemplateIndex((prev) => prev + 1);
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("notifications.birthdayReminder.title")}
				description={t("notifications.birthdayReminder.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			<div
				className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
				data-testid="birthday-reminder-config-container"
			>
				<div className="max-w-3xl mx-auto space-y-6">
					{/* Enable/Disable Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
								<Gift className="h-5 w-5 text-primary shrink-0" />
								<span>{t("notifications.birthdayReminder.enableTitle")}</span>
							</CardTitle>
							<CardDescription>
								{t("notifications.birthdayReminder.enableDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="flex items-center justify-between">
								<Label htmlFor="enabled-switch" className="text-base">
									{t("notifications.birthdayReminder.enableLabel")}
								</Label>
								<ToggleSwitch
									id="enabled-switch"
									checked={enabled}
									onCheckedChange={setEnabled}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Notification Channels Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
								<Mail className="h-5 w-5 text-primary shrink-0" />
								<span>{t("notifications.channels.title")}</span>
							</CardTitle>
							<CardDescription>
								{t("notifications.channels.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{/* Email Channel */}
							<div className="flex items-center justify-between gap-3 p-3 bg-base-200 rounded-lg">
								<div className="flex items-center gap-3 min-w-0 flex-1">
									<Mail className="h-5 w-5 text-primary shrink-0" />
									<div className="min-w-0">
										<p className="font-medium truncate">
											{t("notifications.channels.email")}
										</p>
										<p className="text-sm text-base-content/60 line-clamp-2">
											{t("notifications.channels.emailDescription")}
										</p>
									</div>
								</div>
								<ToggleSwitch
									id="email-enabled-switch"
									checked={emailEnabled}
									onCheckedChange={setEmailEnabled}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Email Template Card */}
					{emailEnabled && (
						<Card>
							<CardHeader className="space-y-3">
								<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
									<div className="min-w-0">
										<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
											<Mail className="h-5 w-5 text-primary shrink-0" />
											<span className="truncate">
												{t("notifications.birthdayReminder.messageTitle")}
											</span>
										</CardTitle>
										<CardDescription className="mt-1.5">
											{t("notifications.birthdayReminder.messageDescription")}
										</CardDescription>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<Button
											variant="outline"
											size="sm"
											className="flex items-center gap-1 text-xs sm:text-sm"
											onClick={handlePopulateFromTemplate}
										>
											<FileText className="h-4 w-4" />
											<span className="hidden xs:inline">
												{t("notifications.birthdayReminder.useTemplateButton")}
											</span>
											<span className="xs:hidden">{t("common.template")}</span>
										</Button>
										<Popover>
											<PopoverTrigger asChild>
												<Button
													variant="outline"
													size="sm"
													className="flex items-center gap-1 text-xs sm:text-sm"
												>
													<HelpCircle className="h-4 w-4" />
													<span className="hidden xs:inline">
														{t(
															"notifications.birthdayReminder.variablesButton",
														)}
													</span>
													<span className="xs:hidden">
														{t("common.variables")}
													</span>
												</Button>
											</PopoverTrigger>
											<PopoverContent
												align="end"
												className="w-[calc(100vw-2rem)] sm:w-80"
											>
												<div className="space-y-3">
													<h4 className="font-medium">
														{t("notifications.birthdayReminder.variablesTitle")}
													</h4>
													<p className="text-sm text-base-content/70">
														{t(
															"notifications.birthdayReminder.variablesDescription",
														)}
													</p>
													<div className="space-y-2">
														{TEMPLATE_VARIABLES.map((item) => (
															<div
																key={item.variable}
																className="flex items-center justify-between gap-2 p-2 bg-base-200 rounded-md"
															>
																<div className="min-w-0 flex-1">
																	<code className="text-xs sm:text-sm font-mono text-primary break-words">
																		{item.variable}
																	</code>
																	<p className="text-xs text-base-content/60 mt-0.5">
																		{t(
																			`notifications.birthdayReminder.variables.${item.variable.replace(/\{\{|\}\}/g, "")}`,
																		)}
																	</p>
																</div>
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-7 px-2 shrink-0"
																	onClick={() => {
																		navigator.clipboard.writeText(
																			item.variable,
																		);
																	}}
																>
																	<Copy className="h-3.5 w-3.5" />
																</Button>
															</div>
														))}
													</div>
												</div>
											</PopoverContent>
										</Popover>
									</div>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="message-subject">
										{t("notifications.birthdayReminder.subjectLabel")}
									</Label>
									<input
										id="message-subject"
										type="text"
										className="input input-bordered w-full"
										placeholder={t(
											"notifications.birthdayReminder.subjectPlaceholder",
										)}
										value={messageSubject}
										onChange={(e) => setMessageSubject(e.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="message-body">
										{t("notifications.birthdayReminder.bodyLabel")}
									</Label>
									<Textarea
										id="message-body"
										className="min-h-32"
										placeholder={t(
											"notifications.birthdayReminder.bodyPlaceholder",
										)}
										value={messageBody}
										onChange={(e) => setMessageBody(e.target.value)}
									/>
									<p className="text-xs text-base-content/60">
										{t("notifications.birthdayReminder.templateHint")}
									</p>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Excluded Customers Card */}
					<Card>
						<CardHeader className="space-y-3">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
									<Users className="h-5 w-5 text-primary shrink-0" />
									<span className="truncate">
										{t("notifications.birthdayReminder.exclusionsTitle")}
									</span>
								</CardTitle>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setIsAddCustomerOpen(true)}
									className="flex items-center gap-1 w-full sm:w-auto justify-center"
								>
									<UserPlus className="h-4 w-4" />
									{t("notifications.birthdayReminder.addExclusion")}
								</Button>
							</div>
							<CardDescription>
								{t("notifications.birthdayReminder.exclusionsDescription")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							{exclusionsData.data.length === 0 ? (
								<p className="text-base-content/60 text-center py-4">
									{t("notifications.birthdayReminder.noExclusions")}
								</p>
							) : (
								<div className="space-y-2">
									{exclusionsData.data.map((exclusion) => (
										<div
											key={exclusion.id}
											className="flex items-start sm:items-center justify-between gap-3 p-3 bg-base-200 rounded-lg"
										>
											<div className="min-w-0 flex-1">
												<p className="font-medium truncate">
													{exclusion.customer?.firstName}{" "}
													{exclusion.customer?.lastName}
												</p>
												{exclusion.customer?.idType &&
													exclusion.customer?.idNumber && (
														<p className="text-sm text-base-content/60 truncate">
															{exclusion.customer.idType}:{" "}
															{exclusion.customer.idNumber}
														</p>
													)}
												{exclusion.customer?.email && (
													<p className="text-sm text-base-content/60 truncate">
														{exclusion.customer.email}
													</p>
												)}
												{exclusion.reason && (
													<p className="text-xs text-base-content/50 mt-1 line-clamp-2">
														{t("notifications.birthdayReminder.reason")}:{" "}
														{exclusion.reason}
													</p>
												)}
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													handleRemoveExclusion(exclusion.customerId)
												}
												disabled={removeExclusionMutation.isPending}
												className="text-error hover:text-error hover:bg-error/10 shrink-0"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									))}
									{exclusionsData.pagination.totalPages > 1 && (
										<p className="text-sm text-base-content/60 text-center pt-2">
											{t("notifications.birthdayReminder.moreExclusions", {
												total: exclusionsData.pagination.total,
											})}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>

					{/* Save Button */}
					<div className="flex justify-center sm:justify-end pb-6">
						<Button
							onClick={handleSave}
							disabled={!hasChanges || updateConfigMutation.isPending}
							className="w-full sm:w-auto min-w-32 text-primary-content"
						>
							{updateConfigMutation.isPending
								? t("common.saving")
								: t("common.save")}
						</Button>
					</div>
				</div>
			</div>

			{/* Add Customer Exclusion Dialog */}
			<Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t("notifications.birthdayReminder.addExclusionTitle")}
						</DialogTitle>
						<DialogDescription>
							{t("notifications.birthdayReminder.addExclusionDescription")}
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						{/* Customer Search */}
						{!selectedCustomer ? (
							<div className="space-y-3">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/50" />
									<Input
										placeholder={t(
											"notifications.birthdayReminder.searchCustomerPlaceholder",
										)}
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-9"
									/>
								</div>

								{debouncedSearchTerm.length > 0 &&
									debouncedSearchTerm.length < 2 && (
										<p className="text-sm text-base-content/60 text-center">
											{t("notifications.birthdayReminder.searchMinChars")}
										</p>
									)}

								{isLoadingCustomers && (
									<div className="flex justify-center py-4">
										<span className="loading loading-spinner loading-sm" />
									</div>
								)}

								{!isLoadingCustomers &&
									debouncedSearchTerm.length >= 2 &&
									customers.length === 0 && (
										<p className="text-sm text-base-content/60 text-center py-4">
											{t("notifications.birthdayReminder.noCustomersFound")}
										</p>
									)}

								{!isLoadingCustomers && customers.length > 0 && (
									<div className="max-h-48 overflow-y-auto space-y-1">
										{customers.map((customer) => {
											const isAlreadyExcluded = excludedCustomerIds.has(
												customer.id,
											);
											return (
												<button
													key={customer.id}
													type="button"
													disabled={isAlreadyExcluded}
													onClick={() => setSelectedCustomer(customer)}
													className={`w-full text-left p-2 rounded-lg transition-colors ${
														isAlreadyExcluded
															? "bg-base-200 opacity-50 cursor-not-allowed"
															: "hover:bg-base-200 cursor-pointer"
													}`}
												>
													<p className="font-medium">
														{customer.firstName} {customer.lastName}
													</p>
													{customer.idType && customer.idNumber && (
														<p className="text-sm text-base-content/60">
															{customer.idType}: {customer.idNumber}
														</p>
													)}
													{customer.email && (
														<p className="text-sm text-base-content/60">
															{customer.email}
														</p>
													)}
													{isAlreadyExcluded && (
														<p className="text-xs text-warning">
															{t(
																"notifications.birthdayReminder.alreadyExcluded",
															)}
														</p>
													)}
												</button>
											);
										})}
									</div>
								)}
							</div>
						) : (
							<div className="space-y-3">
								{/* Selected Customer */}
								<div className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
									<div>
										<p className="font-medium">
											{selectedCustomer.firstName} {selectedCustomer.lastName}
										</p>
										{selectedCustomer.idType && selectedCustomer.idNumber && (
											<p className="text-sm text-base-content/60">
												{selectedCustomer.idType}: {selectedCustomer.idNumber}
											</p>
										)}
										{selectedCustomer.email && (
											<p className="text-sm text-base-content/60">
												{selectedCustomer.email}
											</p>
										)}
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setSelectedCustomer(null)}
									>
										{t("common.change")}
									</Button>
								</div>

								{/* Reason Input */}
								<div className="space-y-2">
									<Label htmlFor="exclusion-reason">
										{t("notifications.birthdayReminder.reasonLabel")}
									</Label>
									<Input
										id="exclusion-reason"
										placeholder={t(
											"notifications.birthdayReminder.reasonPlaceholder",
										)}
										value={exclusionReason}
										onChange={(e) => setExclusionReason(e.target.value)}
									/>
									<p className="text-xs text-base-content/60">
										{t("notifications.birthdayReminder.reasonHint")}
									</p>
								</div>
							</div>
						)}
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsAddCustomerOpen(false)}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleAddExclusion}
							disabled={!selectedCustomer || addExclusionMutation.isPending}
							className="text-primary-content"
						>
							{addExclusionMutation.isPending ? (
								<span className="loading loading-spinner loading-xs" />
							) : (
								<>
									<Plus className="h-4 w-4 mr-1" />
									{t("notifications.birthdayReminder.addExclusionButton")}
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
